<div class="role1-010000-filing-info-container">
  <p-toast></p-toast>
  <p-confirmDialog></p-confirmDialog>
  <app-sheet-loader [visible]="isLoading" />

  <!--
    The gate lives on the CARD, not on the <form> inside it. Gating only the
    form left the card's title and the "General Filing Information" strip
    painted underneath the spinner — both sit inside the card but outside the
    form. <app-sheet-loader> stays a SIBLING above the card so it is not
    removed by this same gate; see scripts/check-sheet-loaders.mjs.
  -->
  <p-card
    header="Filing Information"
    [subheader]="isDev ? '[010000] Filing Information' : ''"
    *ngIf="!isLoading && schema && filingForm"
  >

    <div class="bg-gray-200 text-black-700 text-sm font-medium px-3 py-1">General Filing Information</div>

    <!-- Form Content -->
    <form [formGroup]="filingForm" class="filing-form">

      <!-- Section: General Filing Information -->
      <div class="form-section">
        <div class="form-grid">

          <!-- Render ALL fields in schema order -->
          <ng-container *ngFor="let control of schema.controls">
            <!-- Only show fields that pass shouldShowField() check -->
            <div class="form-field" *ngIf="shouldShowField(control)">
              <label [for]="control.qname" class="field-label">
                {{ control.label }}<app-required-marker [when]="isFieldRequired(control)" />
              </label>

              <!-- Text Input -->
              <div class="field-input-wrapper" *ngIf="control.ui.control === 'text'">
                <input
                  pInputText
                  [id]="control.qname"
                  [formControlName]="control.qname"
                  [placeholder]="control | placeholder"
                  [readonly]="control.ui.readonly || false"
                  class="w-full"
                  [maxlength]="control.ui.maxLength || null"
                  (input)="control.ui.inputFilter ? applyInputFilter($event, control.ui.inputFilter, control.ui.maxLength, control.qname, control.ui.trimLeadingZeros) : null"
                  (paste)="control.ui.inputFilter ? onPasteFilter($event, control.ui.inputFilter, control.ui.maxLength, control.qname, control.ui.trimLeadingZeros) : null"
                />

                <small class="error-message"
                       *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
                  <span *ngIf="getControl(control.qname)?.errors?.['required']">
                    {{ getErrorMessage(control, 'required') }}
                  </span>
                  <span *ngIf="getControl(control.qname)?.errors?.['pattern']">
                    {{ getErrorMessage(control, 'pattern') }}
                  </span>
                </small>

                <!-- Warning messages -->
                <app-warning-message [warnings]="getAllWarnings(control.qname)"></app-warning-message>
              </div>

              <!-- Date Picker -->
              <div class="field-input-wrapper" *ngIf="control.ui.control === 'date'">
                <div [ngClass]="{'datepicker-invalid-wrapper': getControl(control.qname)?.invalid && getControl(control.qname)?.touched && !getControl(control.qname)?.disabled}">
                  <p-datepicker
                    [inputId]="control.qname"
                    [formControlName]="control.qname"
                    dateFormat="yy-mm-dd"
                    [showIcon]="true"
                    iconDisplay="input"
                    [placeholder]="control | fixedPlaceholder: 'Select a date'"
                    class="w-full"
                    [minDate]="getDateFloor(control)"
                    [keepInvalid]="!!getDateFloor(control)"
                    (onInput)="onInputChange($event)"
                    (paste)="onDatePaste($event)"
                  >
                    <!--
                      [keepInvalid] rides on the same predicate as [minDate]:
                      without it PrimeNG renders a below-floor date as an EMPTY
                      input (formatDateTime blanks anything under minDate) while
                      the control still holds it, so a typed or Phase 1
                      extracted pre-2017 end date looked deleted and its inline
                      error explained a field that appeared blank.
                    -->
                    <!--
                      Only the current financial year END date has a [minDate],
                      so this template renders solely for days below the
                      2017-01-31 floor. The tooltip is still gated on the same
                      predicate so a future disabled-date rule cannot inherit
                      this copy by accident.
                    -->
                    <!--
                      `#disabledDate`, not `pTemplate="disabledDate"`: this
                      component imports the standalone `DatePicker`, so the
                      `PrimeTemplate` directive is NOT in scope and a
                      `pTemplate` attribute would be silently ignored. The
                      `#`-form binds through DatePicker's own
                      `@ContentChild('disabledDate')`.
                    -->
                    <ng-template #disabledDate let-dayMeta>
                      <span
                        class="unsupported-date-cell"
                        [pTooltip]="unsupportedFinancialYearEndMessage"
                        tooltipPosition="top"
                        [tooltipDisabled]="!isUnsupportedFinancialYearEndDay(dayMeta)"
                        [attr.aria-label]="
                          isUnsupportedFinancialYearEndDay(dayMeta)
                            ? unsupportedFinancialYearEndMessage
                            : null
                        "
                        >{{ dayMeta.day }}</span
                      >
                    </ng-template>
                  </p-datepicker>
                </div>

                <small class="error-message"
                       *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched && !getControl(control.qname)?.disabled">
                  <!-- Cross-field validation errors for financial year dates -->
                  <span class="block" *ngIf="getControl(control.qname)?.errors?.['dateRangeInvalid']">
                    {{ getControl(control.qname)?.errors?.['dateRangeInvalid'] }}
                  </span>
                  <span class="block" *ngIf="getControl(control.qname)?.errors?.['dateSequenceInvalid']">
                    {{ getControl(control.qname)?.errors?.['dateSequenceInvalid'] }}
                  </span>
                  <!-- Unsupported filing period: financial year end before 2017-01-31 -->
                  <span class="block" *ngIf="getControl(control.qname)?.errors?.['financialYearEndUnsupported']">
                    {{ getControl(control.qname)?.errors?.['financialYearEndUnsupported'] }}
                  </span>
                  <!-- Required field error (suppressed while a date error is shown) -->
                  <span class="block" *ngIf="getControl(control.qname)?.errors?.['required'] && !getControl(control.qname)?.errors?.['dateRangeInvalid'] && !getControl(control.qname)?.errors?.['dateSequenceInvalid']">
                    {{ getErrorMessage(control, 'required') }}
                  </span>
                </small>

                <!-- Warning messages -->
                <app-warning-message [warnings]="getAllWarnings(control.qname)"></app-warning-message>
              </div>

              <!-- Select/Dropdown (with enumSource) -->
              <div class="field-input-wrapper" *ngIf="control.ui.control === 'select' && control.ui.enumSource && !(control.ui.lockWhen && isLockActive(control))">
                <p-select
                  [inputId]="control.qname"
                  [formControlName]="control.qname"
                  [options]="getDropdownOptions(control.ui.enumSource, control.qname)"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="control | fixedPlaceholder: 'Please select'"
                  class="w-full"
                  [filter]="!control.ui.readonly"
                  filterPlaceholder="Search..."
                  [showClear]="true"
                ></p-select>

                <small class="error-message"
                       *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
                  <span *ngIf="getControl(control.qname)?.errors?.['required']">
                    {{ getErrorMessage(control, 'required') }}
                  </span>
                </small>

                <!-- Warning messages -->
                <app-warning-message [warnings]="getAllWarnings(control.qname)"></app-warning-message>
              </div>

              <!--
                Locked text input: when a lockWhen rule is currently active,
                render a disabled text input showing the locked value instead
                of the normal select control. Takes priority over the select
                variants below so the user sees a fixed value (not a dropdown
                they cannot open). Mirrors the role0 implementation.
              -->
              <div class="field-input-wrapper" *ngIf="control.ui.control === 'select' && control.ui.lockWhen && isLockActive(control)">
                <input
                  pInputText
                  [id]="control.qname"
                  [value]="getLockedValue(control)"
                  [disabled]="true"
                  class="w-full"
                />
              </div>

              <!-- Select/Dropdown (with static options) -->
              <div class="field-input-wrapper" *ngIf="control.ui.control === 'select' && control.ui.static && !(control.ui.lockWhen && isLockActive(control))">
                <p-select
                  [inputId]="control.qname"
                  [formControlName]="control.qname"
                  [options]="getStaticOptions(control.ui.static)"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="control | fixedPlaceholder: 'Please select'"
                  class="w-full"
                  [showClear]="true"
                ></p-select>

                <small class="error-message"
                       *ngIf="getControl(control.qname)?.invalid && getControl(control.qname)?.touched">
                  <span *ngIf="getControl(control.qname)?.errors?.['required']">
                    {{ getErrorMessage(control, 'required') }}
                  </span>
                </small>

                <!-- Warning messages -->
                <app-warning-message [warnings]="getAllWarnings(control.qname)"></app-warning-message>
              </div>
            </div>
          </ng-container>

        </div>
      </div>

      <!-- Action Buttons -->
      <!--
        SUBMIT GATE — TWO conditions, both blocking, and together they must
        mirror the guards at the top of this component's onSubmit() exactly.

          !filingForm.valid        — invalid data blocks, whether it was
                                     hydrated at init or introduced by a
                                     debounced edit.
          !hasUnsubmittedChanges() — "no changes detected" blocks (P.O. ruling,
                                     2026-08-17). Submit is available only once
                                     the user has made changes AND those
                                     changes are valid.

        The changes-term REVERSES 759f7d0, which had dropped role1's pristine
        term so that a no-change Phase 2 re-submit stayed available.

        It is `hasUnsubmittedChanges()` — the modified-vs-original verdict that
        also drives the pencil — and NOT `filingForm.pristine`. Read that
        field's docblock in the .ts before changing this: pristine is wrong in
        BOTH directions here (it stays dirty after a hand-revert, and it resets
        to clean when the container's *ngIf remounts this sheet with edits
        still pending). Cancel shares the term for the same reason — after a
        hand-revert its "unsaved changes will be lost" prompt would be lying,
        and after a remount a pristine-gated Cancel would be dead with real
        work to discard.

        DIVERGENCE FROM role0 (Filing Prerequisites), now two-fold: role0 gates
        on `filingForm.pristine`, and WAIVES it on `isOnlyFilingSheet` for a
        fresh Phase 1 conversion. Phase 1 is a single-sheet, single-mount
        surface where the two verdicts cannot diverge, so it was left alone
        deliberately rather than by omission. See
        role0-000000-filing-prerequisites.component.html.

        Do NOT wrap either term in an `isOnlyFilingSheet ? false : …` ternary.
        role1 has no such input by design, and the container clamps
        `filteredSheets` to a single sheet once the gate sheet reports hasErrors
        — which flips the flag true exactly when Submit must stay dead. (That
        clamp is Phase-1-only since 2026-08-24; role0, the flag's only reader,
        is Phase 1 anyway. See SheetApplicabilityService's VALIDITY clamp.) Folding
        `!filingForm.valid` into such a ternary's else-branch is what killed
        the gate in 0981ced.
      -->
      <app-filing-action-buttons
        [submitDisabled]="!filingForm.valid || !hasUnsubmittedChanges()"
        [cancelDisabled]="!hasUnsubmittedChanges()"
        [isSubmitting]="isSubmitting"
        (submit)="onSubmit()"
        (cancel)="onCancel()"
        (delete)="onDelete()"
      >
        <!--
          A Phase 2 conversion only re-runs when Filing Info actually changed,
          so the picker is dead until then. This reuses the SAME term as Submit
          and Cancel above — hasUnsubmittedChanges(), the modified-vs-original
          verdict that also drives the pencil — so role 1 keeps one
          modified-verdict rather than two that can disagree. It is deliberately
          NOT filingForm.pristine; read the comment above this element before
          changing it.
        -->
        <app-conversion-engine-picker
          [disabled]="isEnginePickerDisabled()"
          [lastUsedEngine]="lastUsedEngine"
          (engineChange)="onEngineChange($event)"
        ></app-conversion-engine-picker>
      </app-filing-action-buttons>

    </form>

    <!-- Form Status -->
    <!-- <div *ngIf="!isLoading && schema && filingForm" class="form-status mt-4">
      <p>
        <strong>Form Status:</strong>
        <span [class]="filingForm.valid ? 'text-green-600' : 'text-red-600'">
          {{ filingForm.valid ? 'Valid ✓' : 'Invalid - Please fill required fields' }}
        </span>
      </p>
      <p>
        <strong>Total Fields:</strong> {{ schema.controls.length || 0 }}
      </p>
      <p>
        <strong>Auto-save:</strong>
        <span class="text-green-600">Enabled (500ms debounce)</span>
      </p>
    </div> -->

  </p-card>

  <!-- Delete conversion dialog -->
  <app-delete-conversion
    [(isArchiveDialogOpen)]="isArchiveDialogOpen"
    [conversionData]="conversionData"
    (archiveConversionCompleted)="onDeletionCompleted()"
  ></app-delete-conversion>

  <!-- Edit warning dialog -->
  <app-filing-info-confirmation-dialog
    [(visible)]="isEditWarningDialogOpen"
    (back)="onEditWarningBack()"
    (convert)="onEditWarningConvert()"
  ></app-filing-info-confirmation-dialog>
</div>
